import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { advertisersAPI } from '../lib/api';

// All available platforms in the system
export const ALL_PLATFORMS = [
  'Meta', 'DV360', 'TikTok', 'LinkedIn', 'Twitter', 'Mondiads', 'Propeller', 'Clickadu'
];

/**
 * Hook to get the effective platforms a user can access
 * Returns platforms filtered based on advertiser and user restrictions
 */
export const usePlatformAccess = (advertiserId = null) => {
  const { user, isAdvertiser } = useAuth();
  const [effectivePlatforms, setEffectivePlatforms] = useState(ALL_PLATFORMS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchEffectivePlatforms = useCallback(async () => {
    // Check if user has role that requires advertiser context
    const isAdvertiserOrUser = user?.role === 'advertiser' || user?.role === 'user';
    
    // If user is super_admin or admin without specific advertiser, they see all
    if (!isAdvertiserOrUser && !advertiserId) {
      setEffectivePlatforms(ALL_PLATFORMS);
      setLoading(false);
      return;
    }

    const targetAdvertiserId = advertiserId || user?.advertiser_id;
    if (!targetAdvertiserId) {
      setEffectivePlatforms(ALL_PLATFORMS);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await advertisersAPI.getEffectivePlatforms(targetAdvertiserId);
      setEffectivePlatforms(response.data.effective_platforms || ALL_PLATFORMS);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch effective platforms:', err);
      setError(err);
      // Fallback to all platforms on error
      setEffectivePlatforms(ALL_PLATFORMS);
    } finally {
      setLoading(false);
    }
  }, [advertiserId, user?.advertiser_id, user?.role]);

  useEffect(() => {
    fetchEffectivePlatforms();
  }, [fetchEffectivePlatforms]);

  // Filter function to use on data arrays
  const filterByPlatform = useCallback((data, platformKey = 'platform') => {
    if (!data || !Array.isArray(data)) return data;
    return data.filter(item => effectivePlatforms.includes(item[platformKey]));
  }, [effectivePlatforms]);

  // Check if a specific platform is allowed
  const isPlatformAllowed = useCallback((platform) => {
    return effectivePlatforms.includes(platform);
  }, [effectivePlatforms]);

  return {
    effectivePlatforms,
    loading,
    error,
    filterByPlatform,
    isPlatformAllowed,
    refresh: fetchEffectivePlatforms,
  };
};

export default usePlatformAccess;
